/**
 * Keeps open tabs and installed home-screen apps on the latest published version.
 * Checks the live page for new build files (on load, on return to the app and
 * every few minutes). When a newer version exists, the next page change does a
 * full reload, so nobody loses what they are typing mid-page.
 */
const scriptsIn = (html: string) =>
  new Set(Array.from(html.matchAll(/\/assets\/[\w.-]+\.js/g), (m) => m[0]));

export function loadedBuildScripts(doc: Document = document) {
  return scriptsIn(
    Array.from(doc.querySelectorAll("script[src]"), (s) => s.getAttribute("src") ?? "").join(" "),
  );
}

export function isNewerBuild(current: Set<string>, liveHtml: string) {
  const live = scriptsIn(liveHtml);
  if (!current.size || !live.size) return false;
  return ![...live].some((s) => current.has(s));
}

export function installStaleBuildCheck(): () => void {
  const current = loadedBuildScripts();
  if (!current.size) return () => {}; // dev server: nothing to compare
  let stale = false;
  const check = async () => {
    if (stale || document.visibilityState === "hidden") return;
    try {
      const res = await fetch(`/?v=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
      if (res.ok && isNewerBuild(current, await res.text())) stale = true;
    } catch {
      /* offline — try later */
    }
  };
  const onNav = () => {
    if (stale) window.location.reload();
  };
  const onVis = () => void check();
  const timer = window.setInterval(check, 5 * 60 * 1000);
  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("popstate", onNav);
  const origPush = history.pushState.bind(history);
  history.pushState = (...args: Parameters<History["pushState"]>) => {
    if (stale) {
      const url = args[2];
      if (url) return void window.location.assign(String(url));
    }
    return origPush(...args);
  };
  void check();
  return () => {
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVis);
    window.removeEventListener("popstate", onNav);
    history.pushState = origPush;
  };
}
